import {
  KoButton,
  KoText,
  KoTitle,
  KoToast,
  KoTag,
  KoEmpty,
  KoDateFormatter
} from 'packages';
import KoIcon from '@kwapio/icons';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { add, remove } from 'shared/store/breadcrumbSlice';
import {
  LIFECYCLE_STAGES,
  RELEASE_CLASSIFICATION,
  RISK_LEVEL_OPTIONS,
  RELEASE_TEAM_OPTIONS,
  ENVIRONMENT_PROMOTION,
  DEFAULT_RELEASE_FLOW
} from '../constants';
import ReleaseSubNav from './ReleaseSubNav';
import styles from './releases.module.scss';

const MOCK_RELEASE = {
  releaseId: 'REL-001',
  name: 'Platform Upgrade',
  version: 'v4.2.0',
  classification: 'major',
  participatingTeams: [
    { team: 'api', branch: 'release/api-v3.0' },
    { team: 'web_app', branch: 'release/ui-v4.0' },
    { team: 'backend', branch: 'release/backend-v2.5' }
  ],
  environment: 'qa',
  stage: 'in_qa',
  owner: 'John Doe',
  riskLevel: 'high',
  targetDate: '2026-03-15',
  migrationClassification: 'backward_compatible',
  prsMerged: 12,
  qaPassRate: 85,
  uatProgress: 40,
  gateChecked: 6,
  rollbackStrategy: 'Blue-green deployment with automatic failover. If error rate exceeds 2% within 15 minutes of deploy, auto-rollback to previous stable version via Kubernetes rollout undo.',
  featureFlagPlan: 'New dashboard widgets behind `ff-dashboard-v2` flag. Default OFF in production, ON in staging. Gradual rollout: 10% → 25% → 50% → 100% over 4 days.',
  description: 'Major platform upgrade including new dashboard analytics, redesigned authentication flow, and performance improvements across all API endpoints. Target: 30% reduction in p95 latency.',
  releaseFlow: DEFAULT_RELEASE_FLOW
};

function ReleaseDashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { releaseId, workspaceId } = useParams();

  const [release, setRelease] = useState(null);

  const releaseBasePath = workspaceId
    ? `/workspace/${workspaceId}/development/release`
    : '/release';

  useEffect(() => {
    dispatch(remove());
    dispatch(add([
      { title: 'Home', path: 'home/myoverview' },
      { title: 'Releases', path: releaseBasePath },
      { title: 'Overview' }
    ]));
    setRelease(MOCK_RELEASE);
  }, [releaseId, workspaceId]);

  const handleAdvanceStage = () => {
    const currentIdx = LIFECYCLE_STAGES.findIndex(s => s.key === release?.stage);
    const nextStage = LIFECYCLE_STAGES[currentIdx + 1];
    if (nextStage) {
      setRelease((prev) => ({ ...prev, stage: nextStage.key }));
      KoToast.success('Release advanced to next stage!');
    } else {
      KoToast.error('Cannot advance stage.');
    }
  };

  const currentStageIndex = LIFECYCLE_STAGES.findIndex(s => s.key === release?.stage);
  const classificationConfig = RELEASE_CLASSIFICATION.find(c => c.value === release?.classification) || {};
  const riskConfig = RISK_LEVEL_OPTIONS.find(r => r.value === release?.riskLevel) || {};

  const getTeamLabel = (val) => RELEASE_TEAM_OPTIONS.find(o => o.value === val)?.label || val;

  const stats = [
    { label: 'PRs Merged', value: release?.prsMerged ?? 0, icon: 'PullRequestOutlined', color: 'var(--palette-primary-main)' },
    { label: 'QA Pass Rate', value: `${release?.qaPassRate ?? 0}%`, icon: 'CheckCircleOutlined', color: 'var(--palette-success-main)' },
    { label: 'UAT Progress', value: `${release?.uatProgress ?? 0}%`, icon: 'FileDoneOutlined', color: 'var(--palette-warning-main)' },
    { label: 'Gate Status', value: `${release?.gateChecked ?? 0}/10`, icon: 'LockOutlined', color: 'var(--palette-secondary-main)' }
  ];

  return (
    <div className='tableCard'>
      <ReleaseSubNav activeKey='overview' />

      {!release && (
        <KoEmpty description='Release not found' />
      )}

      {release && (
        <div className={styles.dashboardContent}>
          {/* Release Header */}
          <div className={styles.releaseHeader}>
            <div className={styles.releaseHeaderLeft}>
              <KoTitle level={3} text={release.name || 'Untitled Release'} />
              <div className={styles.releaseHeaderMeta}>
                <KoTag color='blue'>
                  <KoText className={styles.monoSmall} text={release.version} />
                </KoTag>
                {release.classification && (
                  <KoTag color={classificationConfig.color}>{classificationConfig.label}</KoTag>
                )}
                {release.riskLevel && (
                  <KoTag color={riskConfig.color}>Risk: {riskConfig.label}</KoTag>
                )}
                {release.environment && (
                  <KoTag color='geekblue'>{release.environment}</KoTag>
                )}
              </div>
            </div>
            <div className={styles.releaseHeaderRight}>
              <KoButton
                label='Advance Stage'
                type='primary'
                icon={<KoIcon name='ArrowRightOutlined' color='#FFF' width={14} height={14} />}
                onClick={handleAdvanceStage}
              />
            </div>
          </div>

          {/* Lifecycle Stepper */}
          <div className={styles.stepperContainer}>
            <KoText className={styles.sectionLabel} text='RELEASE LIFECYCLE' />
            <div className={styles.stepper}>
              {LIFECYCLE_STAGES.filter(s => s.key !== 'rolled_back').map((stage, idx) => {
                const isActive = stage.key === release?.stage;
                const isCompleted = idx < currentStageIndex;
                const isFuture = idx > currentStageIndex;
                let stepClass = styles.stepItem;
                if (isActive) stepClass += ` ${styles.stepActive}`;
                if (isCompleted) stepClass += ` ${styles.stepCompleted}`;
                if (isFuture) stepClass += ` ${styles.stepFuture}`;
                return (
                  <div key={stage.key} className={stepClass}>
                    <div className={styles.stepCircle}>
                      {isCompleted ? (
                        <KoIcon name='CheckOutlined' width={12} height={12} color='#FFF' />
                      ) : (
                        <span className={styles.stepNumber}>{idx + 1}</span>
                      )}
                    </div>
                    <KoText className={styles.stepLabel} text={stage.label} />
                    {idx < LIFECYCLE_STAGES.length - 2 && (
                      <div className={`${styles.stepLine} ${isCompleted ? styles.stepLineCompleted : ''}`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quick Stats */}
          <div className={styles.statsGrid}>
            {stats.map((s) => (
              <div key={s.label} className={styles.statCard}>
                <div className={styles.statIcon} style={{ color: s.color }}>
                  <KoIcon name={s.icon} width={20} height={20} color={s.color} />
                </div>
                <div className={styles.statInfo}>
                  <span className={styles.statValue} style={{ color: s.color }}>{s.value}</span>
                  <span className={styles.statLabel}>{s.label}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Release Metadata */}
          <div className={styles.metadataSection}>
            <KoText className={styles.sectionLabel} text='RELEASE DETAILS' />
            <div className={styles.metadataGrid}>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Release ID</span>
                <KoText className={styles.monoSmall} text={release.releaseId} />
              </div>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Owner</span>
                <KoText text={release.owner || '-'} />
              </div>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Target Date</span>
                {release.targetDate ? <KoDateFormatter data={release.targetDate} /> : <KoText text='-' />}
              </div>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Migration</span>
                <KoText text={release.migrationClassification || 'None'} />
              </div>
            </div>
          </div>

          {/* Participating Teams & Branches */}
          <div className={styles.metadataSection}>
            <KoText className={styles.sectionLabel} text='PARTICIPATING TEAMS & RELEASE BRANCHES' />
            <div className={styles.teamBranchList}>
              {(release.participatingTeams || []).map((t) => (
                <div key={t.team} className={styles.teamBranchRow}>
                  <div className={styles.teamBranchName}>
                    <KoIcon name='TeamOutlined' width={14} height={14} />
                    <KoText text={getTeamLabel(t.team)} />
                  </div>
                  <div className={styles.teamBranchValue}>
                    <KoIcon name='BranchOutlined' width={12} height={12} />
                    <KoText className={styles.monoSmall} text={t.branch} />
                  </div>
                </div>
              ))}
              {(!release.participatingTeams || release.participatingTeams.length === 0) && (
                <KoText text='No teams assigned' />
              )}
            </div>
          </div>

          {/* Rollback & Feature Flag Plans */}
          {(release.rollbackStrategy || release.featureFlagPlan) && (
            <div className={styles.plansSection}>
              {release.rollbackStrategy && (
                <div className={styles.planCard}>
                  <div className={styles.planCardHeader}>
                    <KoIcon name='RollbackOutlined' width={14} height={14} />
                    <KoText className={styles.planCardTitle} text='Rollback Strategy' />
                  </div>
                  <KoText className={styles.planCardBody} text={release.rollbackStrategy} />
                </div>
              )}
              {release.featureFlagPlan && (
                <div className={styles.planCard}>
                  <div className={styles.planCardHeader}>
                    <KoIcon name='FlagOutlined' width={14} height={14} />
                    <KoText className={styles.planCardTitle} text='Feature Flag Plan' />
                  </div>
                  <KoText className={styles.planCardBody} text={release.featureFlagPlan} />
                </div>
              )}
            </div>
          )}

          {/* Environment Promotion */}
          <div className={styles.envPromotionSection}>
            <KoText className={styles.sectionLabel} text='ENVIRONMENT PROMOTION' />
            <div className={styles.envPromotionGrid}>
              {ENVIRONMENT_PROMOTION.map((env, idx) => {
                const isCurrent = release?.environment === env.key;
                return (
                  <div key={env.key} className={`${styles.envCard} ${isCurrent ? styles.envCardActive : ''}`}>
                    <KoText className={styles.envName} text={env.label} />
                    <KoText className={styles.envSource} text={`Source: ${env.source}`} />
                    <KoText className={styles.envApproval} text={`Approval: ${env.approval}`} />
                    {isCurrent && <KoTag color='success'>Current</KoTag>}
                    {idx < ENVIRONMENT_PROMOTION.length - 1 && (
                      <div className={styles.envArrow}>
                        <KoIcon name='ArrowRightOutlined' width={16} height={16} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Description */}
          {release.description && (
            <div className={styles.descriptionSection}>
              <KoText className={styles.sectionLabel} text='DESCRIPTION / GOALS' />
              <div className={styles.descriptionBox}>
                <KoText text={release.description} />
              </div>
            </div>
          )}

          {/* Release Flow — Quick Navigation */}
          <div className={styles.quickNavSection}>
            <KoText className={styles.sectionLabel} text='RELEASE FLOW' />
            <div className={styles.flowSteps}>
              {(release.releaseFlow || DEFAULT_RELEASE_FLOW).map((step, idx, arr) => {
                const slug = step.label.toLowerCase().replace(/\s+/g, '-');
                const FLOW_PATH_MAP = {
                  'pull-requests': 'pull-requests',
                  'qa-testing': 'qa',
                  'uat-sign-off': 'uat',
                  'release-gate': 'gate',
                  'release-notes': 'release-notes'
                };
                const path = FLOW_PATH_MAP[slug] || slug;
                return (
                  <div key={step.value} className={styles.flowStepItem}>
                    <div
                      className={styles.flowStepCard}
                      style={{ borderTop: `3px solid ${step.color}` }}
                      onClick={() => navigate(`${releaseBasePath}/${releaseId}/${path}`)}
                    >
                      <div className={styles.flowStepNum} style={{ background: step.background, color: step.color }}>{idx + 1}</div>
                      <KoText className={styles.flowStepLabel} text={step.label} />
                      {step.nextStatuses?.length > 0 && (
                        <KoText className={styles.flowStepDesc} text={`Next: ${step.nextStatuses.map(n => n.label).join(', ')}`} />
                      )}
                    </div>
                    {idx < arr.length - 1 && (
                      <div className={styles.flowArrow}>
                        <KoIcon name='ArrowRightOutlined' width={14} height={14} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReleaseDashboard;
